import { InjectionToken } from "@angular/core";
import { Action, HasId, HasType, User } from "shared";

export type MediaType =
	| 'binary'
	| 'image'
	| 'audio'
	| 'video'
	| 'pdf'
	| 'eap'
	| 'link'
	| 'conference'
	| 'folder'
	| 'explorer';

export interface MediaLink {
	readonly src: string;
	readonly mimeType: string;
	readonly size?: number;
}

export interface ImageLink extends MediaLink {
	readonly width: number;
	readonly height: number;
}

export interface AudioLink extends MediaLink {
	readonly duration: number;
	readonly bitrate: number;
	readonly quality: string;
}

export interface VideoLink extends ImageLink, AudioLink {
}

export interface BaseMedia<ML extends MediaLink> extends HasId, HasType {
	readonly id: string;
	readonly type: MediaType; // discriminant property for control-flow type narrowing.
	readonly timeCreated?: number;
	readonly timeUpdated?: number;

	name: string;
	description?: string;
	parentIds?: string[];
	cover: ImageLink;
	overlay?: ImageLink;
	links?: ML[];
	language?: string;
	tags?: string[];

	// dynamic, contextual, extensible metadata
	properties?: {
		download?: boolean;
		order?: number;
		played?: boolean; // user-specific
		[key: string]: any;
	};
	author?: Partial<User>;
	actions?: Action[];
	trackReactions?: boolean;
	displayBadges?: boolean;
}

export interface BinaryMedia extends BaseMedia<MediaLink> {
	type: 'binary';
}

export interface ImageMedia extends BaseMedia<ImageLink> {
	type: 'image';
}

export interface AudioMedia extends BaseMedia<AudioLink> {
	type: 'audio';
}

export interface VideoMedia extends BaseMedia<VideoLink> {
	type: 'video';
	durationSeconds: number;
	hasAudio?: boolean;
}

export interface PdfMedia extends BaseMedia<MediaLink> {
	type: 'pdf';
	pageCount?: number;
}

export interface LinkMedia extends BaseMedia<MediaLink> {
	type: 'link';
	url: string;
	// provider?: string;
	displayMode?: 'same-tab' | 'new-tab';
}

export interface EapMedia extends BaseMedia<MediaLink> {
	type: 'eap';
	displayMode?: 'iframe' | 'inline' | 'new-tab';
}

export interface ConferenceMedia extends BaseMedia<MediaLink> {
	type: 'conference';
}

/**
 * A file explorer over a real sub-directory of the media tree.
 *
 * Unlike a `folder` - which lists sibling media JSON descriptors - an explorer
 * item points at a plain directory on disk (named by the item's `id`, or by
 * `source`) and its contents are read from the filesystem by the backend.
 */
export interface ExplorerMedia extends BaseMedia<MediaLink> {
	type: 'explorer';
	properties?: BaseMedia<MediaLink>['properties'] & {
		explorer?: MediaExplorerRoot;
	};
}

/** Where an explorer item starts browsing, as returned by the backend. */
export interface MediaExplorerRoot {
	readonly area: string;
	/** Directory path relative to the area root, e.g. `wpgh_ads/wpgh_campaign_aug_01`. */
	readonly path: string;
}

/** A single directory entry inside an explorer directory. */
export interface MediaFileEntry {
	readonly name: string;
	readonly kind: 'directory' | 'file';
	/** Path relative to the area root - what to pass back to the browse endpoint. */
	readonly path: string;
	readonly size?: number;
	readonly modified?: number;
	readonly extension?: string;
	readonly mimeType?: string;
	/** Download URL (files only). */
	readonly src?: string;
	/** Server-made preview (images, PDFs and MP4 videos); 404s when none could be made. */
	readonly thumbnail?: string;
}

/** One directory listing. */
export interface MediaDirectory {
	readonly language: string;
	readonly area: string;
	readonly path: string;
	readonly name?: string;
	readonly entries: MediaFileEntry[];
}

export interface FolderMedia extends BaseMedia<any> {
	type: 'folder';
	children: Media[];
	itemCount?: number;
	expandable?: boolean;
}

// discriminated union
export type Media =
	| BinaryMedia
	| ImageMedia
	| AudioMedia
	| VideoMedia
	| PdfMedia
	| LinkMedia
	| EapMedia
	| ConferenceMedia
	| FolderMedia
	| ExplorerMedia;

// Type-safe type guards
export namespace Media {
	export function isFolder(media: Media): media is FolderMedia {
		return media.type === 'folder';
	}

	export function isVideo(media: Media): media is VideoMedia {
		return media.type === 'video';
	}

	export function isAudio(media: Media): media is AudioMedia {
		return media.type === 'audio';
	}

	export function isLink(media: Media): media is LinkMedia {
		return media.type === 'link';
	}

	export function isImage(media: Media): media is ImageMedia {
		return media.type === 'image';
	}

	export function isPdf(media: Media): media is PdfMedia {
		return media.type === 'pdf';
	}

	export function isEap(media: Media): media is EapMedia {
		return media.type === 'eap';
	}

	export function isExplorer(media: Media): media is ExplorerMedia {
		return media.type === 'explorer';
	}
}


export const NULL_MEDIA = new InjectionToken<Media>(
	'NULL_MEDIA',
	{
		providedIn: 'root',
		factory: () => {
			return NULL_MEDIA_VALUE; //new NullMedia()
		}
	}
);

export const NULL_MEDIA_VALUE: BinaryMedia = {
	id: '0',
	type: 'binary',
	name: '',
	cover: {
		src: '',
		mimeType: 'application/octet-stream',
		width: 1,
		height: 1
	}
};

// export class NullMedia implements BinaryMedia {
// 	readonly id = '0';
// 	readonly type: 'binary' = 'binary';

// 	name = '';
// 	description?: string;
// 	cover!: ImageLink;

// 	timeCreated = undefined;
// 	timeUpdated = undefined;
// 	links: MediaLink[] = [];
// 	actions = [];
// 	properties = {};
// 	language = undefined;

// 	constructor(init?: Partial<Media>) {
// 		Object.assign(this, init);
// 	}
// }